import { useState } from 'react';
import type { Channel, PublicUser } from '@itsappy/shared';
import { Avatar, Modal, Spinner } from '@/components/ui';
import { useInviteChannelMembers } from '@/hooks/queries';
import { toast } from '@/store/ui';

export function ChannelInviteModal({
  channel,
  members,
  open,
  onClose,
}: {
  channel: Channel;
  members: PublicUser[];
  open: boolean;
  onClose: () => void;
}) {
  const invite = useInviteChannelMembers(channel.id);
  const [selected, setSelected] = useState<string[]>([]);
  const available = members.filter((member) => !channel.memberIds.includes(member.id));

  function close() {
    setSelected([]);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Invite people to #${channel.name}`}
      description="Invited people can open the channel and read its existing message history."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!selected.length || invite.isPending}
            onClick={async () => {
              try {
                await invite.mutateAsync(selected);
                toast.success(selected.length === 1 ? 'Person invited' : `${selected.length} people invited`);
                close();
              } catch (error) {
                toast.error('Could not invite people', error instanceof Error ? error.message : undefined);
              }
            }}
          >
            {invite.isPending && <Spinner />}
            Invite {selected.length || ''}
          </button>
        </>
      }
    >
      {available.length === 0 ? (
        <p className="text-sm text-slate-400">Everyone in the workspace is already in this channel.</p>
      ) : (
        <div className="max-h-80 space-y-1 overflow-y-auto overscroll-contain">
          {available.map((member) => (
            <label key={member.id} className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-surface-2">
              <input
                type="checkbox"
                checked={selected.includes(member.id)}
                onChange={() => setSelected((current) =>
                  current.includes(member.id)
                    ? current.filter((id) => id !== member.id)
                    : [...current, member.id])}
                className="accent-brand-500"
              />
              <Avatar user={member} size="sm" showStatus />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-slate-100">{member.name}</span>
                <span className="block truncate text-xs text-slate-500">{member.title}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}
